import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import {
  Cake, Clock, UserX, UserPlus, Play, Save, ChevronDown, ChevronUp,
  History, TrendingUp, Gift as GiftIcon, CalendarClock, Zap, Repeat,
  ShoppingBag, Award, Users, Lock, Eye, MessageCircle, Power, Settings2,
  Star, Trophy, Euro, Gamepad2, Flame, Bell, Smartphone, Mail, BarChart3
} from 'lucide-react';
import { AUTOMATION_META, CHANNEL_PREVIEWS, AUTOMATION_VARS, COMMON_VARS, CATEGORIES, CH_LIST } from '../components/automations/automationConfig';
import { PageTooltip } from '../components/TooltipSystem';

/* ── ReviewPlatformSelect ── */
function ReviewPlatformSelect({ value, onChange, testId }) {
  const [links, setLinks] = React.useState([]);
  React.useEffect(() => {
    adminApi.get('/admin/review-links').then(r => setLinks(r.data?.links || (Array.isArray(r.data) ? r.data : []))).catch(() => {});
  }, []);
  return (
    <div className="relative">
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        data-testid={testId}
        className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] appearance-none focus:border-[#D44E20] focus:outline-none transition-colors"
      >
        <option value="">-- Seleziona piattaforma --</option>
        {links.map(l => <option key={l.platform} value={l.platform}>{l.platform}</option>)}
      </select>
      <ChevronDown size={12} className="absolute right-3 top-[10px] text-[#aaa] pointer-events-none" />
      {links.length === 0 && (
        <p className="text-[10px] text-amber-600 mt-1">Nessun link configurato. Vai su Contatti &gt; Link Recensioni per aggiungerne.</p>
      )}
    </div>
  );
}

/* ── CouponMultiSelect for expiry reminder ── */
function CouponMultiSelect({ value, onChange, testId }) {
  const [coupons, setCoupons] = React.useState([]);
  React.useEffect(() => {
    adminApi.get('/broadcast/active-coupons')
      .then(r => setCoupons((r.data || []).filter(c => c.valid_until)))
      .catch(() => {});
  }, []);
  const selected = value || [];
  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    onChange(next);
  };
  return (
    <div data-testid={testId}>
      {coupons.length === 0 ? (
        <p className="text-[10px] text-amber-600">Nessun coupon attivo con scadenza. Crea un coupon con data di scadenza.</p>
      ) : (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {coupons.map(c => {
            const sel = selected.includes(c.id);
            return (
              <label key={c.id} data-testid={`coupon-option-${c.id}`}
                className={`flex items-center gap-2.5 p-2.5 rounded-xl border cursor-pointer transition-all ${sel ? 'border-[#DC2626]/30 bg-red-50/50' : 'border-slate-100 bg-slate-50 hover:border-slate-200'}`}>
                <input type="checkbox" checked={sel} onChange={() => toggle(c.id)} className="sr-only" />
                <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${sel ? 'bg-[#DC2626] border-[#DC2626]' : 'border-gray-300 bg-white'}`}>
                  {sel && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[#1A1A1A] truncate">{c.code} — {c.reward_label}</p>
                  <p className="text-[10px] text-[#64748B]">Scadenza: {c.valid_until}</p>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── ChannelSelectorMini (inline in automation card) ── */
function ChannelSelectorMini({ channels, onChange, type }) {
  const toggle = (id) => {
    if (CH_LIST.find(c => c.id === id)?.disabled) return;
    const next = channels.includes(id) ? channels.filter(c => c !== id) : [...channels, id];
    if (next.length === 0) return;
    onChange(next);
  };
  return (
    <div data-testid={`channels-${type}`}>
      <label className="text-[11px] text-[#64748B] font-medium block mb-1.5">Canali di invio</label>
      <div className="flex flex-wrap gap-1.5">
        {CH_LIST.map(ch => {
          const Icon = ch.icon;
          const active = channels.includes(ch.id);
          return (
            <button key={ch.id} onClick={() => toggle(ch.id)} disabled={ch.disabled}
              data-testid={`channel-${type}-${ch.id}`}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-all ${
                ch.disabled ? 'opacity-40 cursor-not-allowed border-slate-100 bg-slate-50 text-[#aaa]' :
                active ? 'border-current/30 shadow-sm' : 'border-slate-100 bg-slate-50 text-[#64748B] hover:border-slate-200'
              }`}
              style={active && !ch.disabled ? { borderColor: `${ch.color}40`, backgroundColor: `${ch.color}08`, color: ch.color } : {}}>
              <Icon size={12} /> {ch.label}
              {ch.disabled && <span className="text-[9px] italic ml-0.5">Presto</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Render preview text with highlighted variables ── */
function renderPreviewText(text) {
  return text.split(/(\{\{[^}]+\}\})/).map((part, i) => {
    if (part.startsWith('{{') && part.endsWith('}}')) {
      return <span key={i} className="font-mono font-bold text-[#D44E20] bg-[#FFF5F2] px-0.5 rounded text-[10px]">{part}</span>;
    }
    return part;
  });
}

/* ── Field Insert Dropdown ── */
function FieldInsertDropdown({ type, onInsert, resources }) {
  const [open, setOpen] = React.useState(false);
  const [resOpen, setResOpen] = React.useState(null); // 'coupon' | 'challenge'
  const ref = React.useRef(null);
  const typeVars = AUTOMATION_VARS[type] || [];

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setResOpen(null); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const insert = (key) => { onInsert(`{{${key}}}`); setOpen(false); };
  const insertText = (text) => { onInsert(text); setOpen(false); setResOpen(null); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); setResOpen(null); }}
        data-testid={`field-insert-btn-${type}`}
        className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-[#D44E20] bg-[#FFF5F2] border border-[#D44E20]/20 rounded-lg hover:bg-[#FDECE7] transition-colors"
      >
        <ChevronDown size={10} /> Inserisci campo
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden" data-testid={`field-dropdown-${type}`}>
          <div className="max-h-80 overflow-y-auto p-1.5">
            <p className="text-[9px] text-[#94A3B8] font-bold uppercase tracking-wider px-2 py-1">Cliente</p>
            {COMMON_VARS.map(v => (
              <button key={v.key} onClick={() => insert(v.key)} className="w-full text-left px-2.5 py-1.5 rounded-lg text-[11px] hover:bg-slate-50 flex items-center gap-2 transition-colors">
                <span className="font-mono text-[#D44E20] bg-[#FFF5F2] px-1 rounded text-[10px]">{`{{${v.key}}}`}</span>
                <span className="text-[#64748B]">{v.label}</span>
              </button>
            ))}
            {typeVars.length > 0 && (
              <>
                <div className="border-t border-slate-100 my-1" />
                <p className="text-[9px] text-[#94A3B8] font-bold uppercase tracking-wider px-2 py-1">Automazione</p>
                {typeVars.map(v => (
                  <button key={v.key} onClick={() => insert(v.key)} className="w-full text-left px-2.5 py-1.5 rounded-lg text-[11px] hover:bg-slate-50 flex items-center gap-2 transition-colors">
                    <span className="font-mono text-[#D44E20] bg-[#FFF5F2] px-1 rounded text-[10px]">{`{{${v.key}}}`}</span>
                    <span className="text-[#64748B]">{v.label}</span>
                  </button>
                ))}
              </>
            )}
            {/* Resource insertion */}
            {(resources?.coupons?.length > 0 || resources?.challenges?.length > 0) && (
              <>
                <div className="border-t border-slate-100 my-1" />
                <p className="text-[9px] text-[#94A3B8] font-bold uppercase tracking-wider px-2 py-1">Risorse</p>
                {resources?.coupons?.length > 0 && (
                  <div>
                    <button onClick={() => setResOpen(resOpen === 'coupon' ? null : 'coupon')} className="w-full text-left px-2.5 py-1.5 rounded-lg text-[11px] hover:bg-slate-50 flex items-center justify-between transition-colors">
                      <span className="text-[#64748B] flex items-center gap-1.5"><span className="text-[13px]">🎟</span> Coupon attivi</span>
                      <ChevronDown size={10} className={`text-[#aaa] transition-transform ${resOpen === 'coupon' ? 'rotate-180' : ''}`} />
                    </button>
                    {resOpen === 'coupon' && (
                      <div className="ml-3 space-y-0.5 pb-1">
                        {resources.coupons.map(c => (
                          <button key={c.id} onClick={() => insertText(c.code)} className="w-full text-left px-2.5 py-1.5 rounded-lg text-[11px] hover:bg-amber-50 flex items-center justify-between transition-colors">
                            <span><span className="font-mono font-bold text-[#1A1A1A]">{c.code}</span> <span className="text-[#94A3B8]">— {c.reward_label}</span></span>
                            {c.valid_until && <span className="text-[9px] text-[#94A3B8]">{c.valid_until}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {resources?.challenges?.length > 0 && (
                  <div>
                    <button onClick={() => setResOpen(resOpen === 'challenge' ? null : 'challenge')} className="w-full text-left px-2.5 py-1.5 rounded-lg text-[11px] hover:bg-slate-50 flex items-center justify-between transition-colors">
                      <span className="text-[#64748B] flex items-center gap-1.5"><Trophy size={12} /> Challenge attive</span>
                      <ChevronDown size={10} className={`text-[#aaa] transition-transform ${resOpen === 'challenge' ? 'rotate-180' : ''}`} />
                    </button>
                    {resOpen === 'challenge' && (
                      <div className="ml-3 space-y-0.5 pb-1">
                        {resources.challenges.map(c => (
                          <button key={c.id} onClick={() => insertText(c.name)} className="w-full text-left px-2.5 py-1.5 rounded-lg text-[11px] hover:bg-emerald-50 flex items-center justify-between transition-colors">
                            <span className="font-semibold text-[#1A1A1A]">{c.name}</span>
                            {c.end_date && <span className="text-[9px] text-[#94A3B8]">{c.end_date}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Single Channel Editor Block ── */
function ChannelEditorBlock({ channel, type, config, defaultPreview, onFieldChange, resources }) {
  const textareaRef = React.useRef(null);

  const chConfig = {
    sms: { icon: Smartphone, color: '#F59E0B', bg: 'bg-amber-50/60', border: 'border-amber-100/80', label: 'SMS', bodyKey: 'sms_custom' },
    push: { icon: Bell, color: '#6366F1', bg: 'bg-indigo-50/60', border: 'border-indigo-100/80', label: 'PUSH', titleKey: 'push_title_custom', bodyKey: 'push_body_custom' },
    email: { icon: Mail, color: '#3B82F6', bg: 'bg-blue-50/60', border: 'border-blue-100/80', label: 'EMAIL', subjectKey: 'email_subject_custom', bodyKey: 'email_body_custom' },
  }[channel];
  if (!chConfig) return null;

  const ChIcon = chConfig.icon;
  const bodyVal = config[chConfig.bodyKey] ?? '';
  const titleVal = chConfig.titleKey ? (config[chConfig.titleKey] ?? '') : '';
  const subjectVal = chConfig.subjectKey ? (config[chConfig.subjectKey] ?? '') : '';
  const placeholder = defaultPreview?.body || '';
  const titlePlaceholder = defaultPreview?.pushTitle || '';
  const subjectPlaceholder = defaultPreview?.emailSubject || '';

  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) { onFieldChange(type, chConfig.bodyKey, (bodyVal || '') + text); return; }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const current = bodyVal || '';
    const newVal = current.substring(0, start) + text + current.substring(end);
    onFieldChange(type, chConfig.bodyKey, newVal);
    setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + text.length; ta.focus(); }, 0);
  };

  const handleReset = () => {
    onFieldChange(type, chConfig.bodyKey, '');
    if (chConfig.titleKey) onFieldChange(type, chConfig.titleKey, '');
    if (chConfig.subjectKey) onFieldChange(type, chConfig.subjectKey, '');
  };

  return (
    <div className={`${chConfig.bg} border ${chConfig.border} rounded-xl p-3 transition-all`} data-testid={`editor-${channel}-${type}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <ChIcon size={12} style={{ color: chConfig.color }} />
          <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: chConfig.color }}>{chConfig.label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <FieldInsertDropdown type={type} onInsert={insertAtCursor} resources={resources} />
          {(bodyVal || titleVal || subjectVal) && (
            <button onClick={handleReset} data-testid={`reset-${channel}-${type}`}
              className="text-[10px] text-[#94A3B8] hover:text-[#D44E20] transition-colors font-medium">
              Ripristina
            </button>
          )}
        </div>
      </div>
      {/* Title/Subject field for Push/Email */}
      {channel === 'push' && (
        <input
          value={titleVal}
          onChange={e => onFieldChange(type, chConfig.titleKey, e.target.value)}
          placeholder={titlePlaceholder}
          data-testid={`push-title-${type}`}
          className="w-full h-8 bg-white/70 border border-indigo-100 rounded-lg px-2.5 text-[11px] font-semibold text-[#1A1A1A] placeholder:text-[#bbb] focus:border-indigo-300 focus:outline-none mb-1.5 transition-colors"
        />
      )}
      {channel === 'email' && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="text-[10px] text-[#64748B] flex-shrink-0">Oggetto:</span>
          <input
            value={subjectVal}
            onChange={e => onFieldChange(type, chConfig.subjectKey, e.target.value)}
            placeholder={subjectPlaceholder}
            data-testid={`email-subject-${type}`}
            className="flex-1 h-8 bg-white/70 border border-blue-100 rounded-lg px-2.5 text-[11px] font-semibold text-[#1A1A1A] placeholder:text-[#bbb] focus:border-blue-300 focus:outline-none transition-colors"
          />
        </div>
      )}
      {/* Body textarea */}
      <textarea
        ref={textareaRef}
        value={bodyVal}
        onChange={e => onFieldChange(type, chConfig.bodyKey, e.target.value)}
        placeholder={placeholder}
        data-testid={`${channel}-body-${type}`}
        rows={3}
        className="w-full bg-white/70 border border-slate-200/60 rounded-lg p-2.5 text-[11px] text-[#1A1A1A] placeholder:text-[#bbb] focus:border-current/30 focus:outline-none resize-none leading-relaxed transition-colors"
        style={{ borderColor: bodyVal ? `${chConfig.color}30` : undefined }}
      />
      {!bodyVal && (
        <p className="text-[9px] text-[#94A3B8] mt-1 italic">Lascia vuoto per usare il messaggio predefinito</p>
      )}
      {channel === 'email' && <p className="text-[9px] text-[#94A3B8] mt-1 italic">Footer: — Il tuo locale (aggiunto automaticamente)</p>}
    </div>
  );
}

/* ── ChannelMessageEditor (replaces ChannelMessagePreview) ── */
function ChannelMessageEditor({ type, channels, config, onFieldChange, resources }) {
  const preview = CHANNEL_PREVIEWS[type];
  const nonWa = (channels || ['whatsapp']).filter(c => c !== 'whatsapp');
  if (nonWa.length === 0 || !preview) return null;

  return (
    <div className="space-y-2" data-testid={`channel-editor-${type}`}>
      <label className="text-[11px] text-[#64748B] font-medium flex items-center gap-1.5">
        <Eye size={11} className="text-[#94A3B8]" />
        Messaggi personalizzati per canale
      </label>
      {nonWa.map(ch => (
        <ChannelEditorBlock
          key={ch}
          channel={ch}
          type={type}
          config={config}
          defaultPreview={preview}
          onFieldChange={onFieldChange}
          resources={resources}
        />
      ))}
    </div>
  );
}

/* ── AutomationCard ── */
function AutomationCard({ type, meta, config, expanded, onToggleExpand, onToggle, onSave, onRun, saving, running, onFieldChange, rewards, schedStatus, costInfo, resources }) {
  const Icon = meta.icon;
  const isExpanded = expanded === type;
  const isEventTriggered = meta.isEventTriggered;
  const hasConfig = meta.fields.length > 0;

  return (
    <div
      className={`bg-white rounded-2xl border overflow-hidden transition-all duration-200 ${
        isExpanded ? 'border-[#D44E20]/20 shadow-md' : 'border-slate-100 shadow-sm hover:shadow-md'
      }`}
      data-testid={`automation-card-${type}`}
    >
      {/* Header */}
      <div className="p-4 md:p-5 flex items-center gap-3 md:gap-4">
        <div className={`w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.bgColor}`}>
          <Icon size={18} style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3 className="font-semibold text-[#1A1A1A] text-sm">{meta.title}</h3>
            {isEventTriggered && (
              <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 tracking-wide">Evento</span>
            )}
            {!isEventTriggered && config.enabled && config.schedule_time && (
              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-md bg-slate-100 text-[#64748B] flex items-center gap-0.5">
                <Clock size={8} /> {config.schedule_time}
              </span>
            )}
            {schedStatus?.ran_today && (
              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-600">Eseguita oggi</span>
            )}
            {costInfo && (
              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 flex items-center gap-0.5" data-testid={`cost-badge-${type}`}>
                <Euro size={8} />
                {costInfo.type === 'per_event'
                  ? `${costInfo.cost_per_msg.toFixed(4)}/msg`
                  : costInfo.eligible > 0
                    ? `~${costInfo.total_cost.toFixed(2)} (${costInfo.label})`
                    : '0 dest.'}
              </span>
            )}
            {/* Channel badges in collapsed state */}
            {(config.channels || ['whatsapp']).map(ch => {
              const chMeta = CH_LIST.find(c => c.id === ch);
              if (!chMeta) return null;
              const ChIcon = chMeta.icon;
              return <span key={ch} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-medium" style={{ backgroundColor: `${chMeta.color}10`, color: chMeta.color }}><ChIcon size={8} />{chMeta.label}</span>;
            })}
          </div>
          <p className="text-[#999] text-xs mt-0.5 line-clamp-1">{meta.desc}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onToggle(type)}
            data-testid={`toggle-${type}`}
            className={`w-11 h-6 rounded-full transition-all relative ${config.enabled ? 'bg-[#3ECF8E]' : 'bg-gray-200'}`}
          >
            <div className={`w-[18px] h-[18px] bg-white rounded-full absolute top-[3px] transition-all shadow-sm ${config.enabled ? 'left-[22px]' : 'left-[3px]'}`} />
          </button>
          {(hasConfig || meta.isTemplate) && (
            <button
              onClick={() => onToggleExpand(isExpanded ? null : type)}
              data-testid={`expand-${type}`}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-[#aaa] hover:bg-slate-100 hover:text-[#64748B] transition-colors"
            >
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 md:px-5 pb-5 border-t border-gray-50 pt-4 space-y-4">
          {/* Event trigger info */}
          {isEventTriggered && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50/60 border border-amber-100 rounded-xl">
              <Zap size={12} className="text-amber-600 flex-shrink-0" />
              <p className="text-[11px] text-amber-800">Si attiva automaticamente quando il cliente raggiunge il traguardo.</p>
            </div>
          )}

          {/* Schedule time */}
          {!isEventTriggered && (
            <div>
              <label className="text-[11px] text-[#64748B] font-medium block mb-1.5">Orario esecuzione giornaliera (Italia)</label>
              <input
                type="time"
                value={config.schedule_time || '09:00'}
                onChange={e => onFieldChange(type, 'schedule_time', e.target.value)}
                data-testid={`schedule-time-${type}`}
                className="w-36 h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none transition-colors"
              />
            </div>
          )}

          {/* Template preview */}
          {meta.isTemplate && (
            <div className="space-y-2.5">
              <div className="flex items-center gap-1.5">
                <Lock size={11} className="text-[#aaa]" />
                <span className="text-[10px] text-[#aaa] font-medium">Template Meta approvato — non modificabile</span>
              </div>
              <div className="bg-[#E7F8EC] border border-[#C8E6C9] rounded-xl p-3.5 relative">
                <Eye size={12} className="text-[#4CAF50] absolute top-2.5 right-2.5" />
                <p className="text-[12px] text-[#1B5E20] whitespace-pre-line leading-relaxed font-mono pr-6">
                  {meta.templateText}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {meta.templateVars.map(v => (
                  <span key={v.placeholder} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-100 rounded-md text-[10px] text-[#64748B]">
                    <span className="font-mono font-bold text-[#D44E20]">{v.placeholder}</span>
                    <span>= {v.label}</span>
                    <span className="text-[9px] text-[#aaa] ml-0.5">{v.auto ? '(auto)' : '(edit)'}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Config fields */}
          {hasConfig && (
            <>
              {meta.isTemplate && <div className="border-t border-gray-50" />}
              <div className="space-y-3">
                {meta.fields.map(field => {
                  if (field.showIf && !field.showIf(config)) return null;
                  return (
                    <div key={field.key}>
                      <label className="text-[11px] text-[#64748B] font-medium block mb-1">{field.label}</label>
                      {field.type === 'number' && (
                        <input
                          type="number"
                          value={config[field.key] ?? ''}
                          onChange={e => onFieldChange(type, field.key, parseInt(e.target.value) || 0)}
                          data-testid={`field-${type}-${field.key}`}
                          className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none transition-colors"
                        />
                      )}
                      {field.type === 'text' && (
                        <input
                          type="text"
                          value={config[field.key] ?? ''}
                          onChange={e => onFieldChange(type, field.key, e.target.value)}
                          placeholder={field.placeholder}
                          data-testid={`field-${type}-${field.key}`}
                          className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] placeholder:text-[#bbb] focus:border-[#D44E20] focus:outline-none transition-colors"
                        />
                      )}
                      {field.type === 'textarea' && (
                        <textarea
                          value={config[field.key] ?? ''}
                          onChange={e => onFieldChange(type, field.key, e.target.value)}
                          placeholder={field.placeholder}
                          data-testid={`field-${type}-${field.key}`}
                          rows={3}
                          className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm text-[#1A1A1A] placeholder:text-[#bbb] focus:border-[#D44E20] focus:outline-none resize-none transition-colors"
                        />
                      )}
                      {field.type === 'select' && (
                        <div className="relative">
                          <select
                            value={config[field.key] ?? ''}
                            onChange={e => onFieldChange(type, field.key, e.target.value)}
                            data-testid={`field-${type}-${field.key}`}
                            className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] appearance-none focus:border-[#D44E20] focus:outline-none transition-colors"
                          >
                            {field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          <ChevronDown size={12} className="absolute right-3 top-[10px] text-[#aaa] pointer-events-none" />
                        </div>
                      )}
                      {field.type === 'reward_select' && (
                        <div className="relative">
                          <select
                            value={config[field.key] ?? ''}
                            onChange={e => onFieldChange(type, field.key, e.target.value)}
                            data-testid={`field-${type}-${field.key}`}
                            className="w-full h-9 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] appearance-none focus:border-[#D44E20] focus:outline-none transition-colors"
                          >
                            <option value="">-- Seleziona un premio --</option>
                            {rewards.map(r => <option key={r.id} value={r.id}>{r.name} ({r.points_cost} pt)</option>)}
                          </select>
                          <ChevronDown size={12} className="absolute right-3 top-[10px] text-[#aaa] pointer-events-none" />
                        </div>
                      )}
                      {field.type === 'review_platform_select' && (
                        <ReviewPlatformSelect
                          value={config[field.key] ?? ''}
                          onChange={val => onFieldChange(type, field.key, val)}
                          testId={`field-${type}-${field.key}`}
                        />
                      )}
                      {field.type === 'coupon_multi_select' && (
                        <CouponMultiSelect
                          value={config[field.key] ?? []}
                          onChange={val => onFieldChange(type, field.key, val)}
                          testId={`field-${type}-${field.key}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Channel selector */}
          <div className="pt-1">
            <ChannelSelectorMini
              channels={config.channels || ['whatsapp']}
              onChange={(ch) => onFieldChange(type, 'channels', ch)}
              type={type}
            />
          </div>

          {/* Channel message editors (SMS/Push/Email) */}
          <ChannelMessageEditor type={type} channels={config.channels || ['whatsapp']} config={config} onFieldChange={onFieldChange} resources={resources} />

          {/* Actions — always show Save when expanded for channel config */}
          <div className="flex gap-2.5 pt-1">
            <button
              onClick={() => onSave(type)}
              disabled={saving[type]}
              data-testid={`save-${type}`}
              className="flex-1 h-9 bg-[#D44E20] text-white text-sm font-semibold rounded-xl flex items-center justify-center gap-1.5 hover:bg-[#C04018] disabled:opacity-50 transition-colors"
            >
              <Save size={13} /> {saving[type] ? 'Salvataggio...' : 'Salva'}
            </button>
            {config.enabled && !isEventTriggered && (
              <button
                onClick={() => onRun(type)}
                disabled={running[type]}
                data-testid={`run-${type}`}
                className="flex-1 h-9 bg-white border border-slate-200 text-[#64748B] text-sm font-semibold rounded-xl flex items-center justify-center gap-1.5 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                <Play size={13} /> {running[type] ? 'Esecuzione...' : 'Esegui ora'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main Page ── */
export default function AdminAutomations() {
  const [automations, setAutomations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState({});
  const [running, setRunning] = useState({});
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [schedulerInfo, setSchedulerInfo] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [costData, setCostData] = useState(null);
  const [resources, setResources] = useState({ coupons: [], challenges: [] });

  useEffect(() => {
    Promise.all([
      adminApi.get('/automations'),
      adminApi.get('/automations/logs'),
      adminApi.get('/automations/scheduler-status'),
      adminApi.get('/rewards'),
      adminApi.get('/admin/marketing/automation-costs'),
      adminApi.get('/broadcast/active-coupons').catch(() => ({ data: [] })),
      adminApi.get('/broadcast/active-challenges').catch(() => ({ data: [] })),
    ]).then(([aRes, lRes, sRes, rRes, cRes, cpRes, chRes]) => {
      setAutomations(aRes.data);
      setLogs(lRes.data);
      setSchedulerInfo(sRes.data);
      setRewards(rRes.data || []);
      setCostData(cRes.data);
      setResources({ coupons: cpRes.data || [], challenges: chRes.data || [] });
    }).catch(() => toast.error('Errore caricamento'))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (type) => {
    const current = automations[type];
    const newEnabled = !current.enabled;
    // Optimistic update
    setAutomations(prev => ({ ...prev, [type]: { ...prev[type], enabled: newEnabled } }));
    try {
      await adminApi.put(`/automations/${type}`, { enabled: newEnabled });
      toast.success(newEnabled ? `${AUTOMATION_META[type].title} attivata` : `${AUTOMATION_META[type].title} disattivata`);
    } catch (err) {
      // Revert on error
      setAutomations(prev => ({ ...prev, [type]: { ...prev[type], enabled: !newEnabled } }));
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleSave = async (type) => {
    setSaving(s => ({ ...s, [type]: true }));
    try {
      const config = automations[type];
      const body = { schedule_time: config.schedule_time };
      AUTOMATION_META[type].fields.forEach(f => {
        if (config[f.key] !== undefined) body[f.key] = config[f.key];
      });
      if (config.channels) body.channels = config.channels;
      // Custom per-channel messages
      ['sms_custom', 'push_title_custom', 'push_body_custom', 'email_subject_custom', 'email_body_custom'].forEach(k => {
        if (config[k] !== undefined && config[k] !== null) body[k] = config[k];
      });
      await adminApi.put(`/automations/${type}`, body);
      toast.success('Configurazione salvata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally { setSaving(s => ({ ...s, [type]: false })); }
  };

  const handleRun = async (type) => {
    setRunning(r => ({ ...r, [type]: true }));
    try {
      const res = await adminApi.post(`/automations/run/${type}`);
      toast.success(res.data.message);
      const lRes = await adminApi.get('/automations/logs');
      setLogs(lRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally { setRunning(r => ({ ...r, [type]: false })); }
  };

  const updateField = (type, key, value) => {
    setAutomations(a => ({ ...a, [type]: { ...a[type], [key]: value } }));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // Count active automations
  const allTypes = Object.keys(AUTOMATION_META);
  const activeCount = allTypes.filter(t => automations?.[t]?.enabled).length;

  // Filter automations by active tab
  const filteredTypes = activeTab === 'all'
    ? allTypes
    : allTypes.filter(t => AUTOMATION_META[t].category === activeTab);

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-3xl" data-testid="admin-automations-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl tracking-tight font-bold text-[#1A1A1A]">Messaggi automatici</h1>
          <p className="text-[#999] text-xs mt-0.5">Attiva messaggi automatici che vengono inviati in base all'attività dei clienti</p>
        </div>
        <button
          onClick={() => setShowLogs(!showLogs)}
          data-testid="toggle-logs-btn"
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-[#64748B] hover:bg-slate-50 transition-colors shadow-sm"
        >
          <History size={14} /> Storico
        </button>
      </div>

      {/* Stats + Scheduler */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-100 rounded-xl shadow-sm">
          <Power size={13} className="text-[#3ECF8E]" />
          <span className="text-xs font-semibold text-[#1A1A1A]" data-testid="active-count">{activeCount}/{allTypes.length}</span>
          <span className="text-[10px] text-[#999]">attive</span>
        </div>
        {schedulerInfo && (
          <div className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-100 rounded-xl shadow-sm" data-testid="scheduler-status-banner">
            <div className={`w-1.5 h-1.5 rounded-full ${schedulerInfo.scheduler_running ? 'bg-[#3ECF8E]' : 'bg-red-400'}`} />
            <span className="text-xs font-medium text-[#1A1A1A]">
              Scheduler {schedulerInfo.scheduler_running ? 'attivo' : 'fermo'}
            </span>
            <span className="text-[10px] text-[#999]">{schedulerInfo.italy_time}</span>
          </div>
        )}
      </div>

      {/* Category Tabs */}
      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="category-tabs">
        {CATEGORIES.map(cat => {
          const CatIcon = cat.icon;
          const count = cat.key === 'all'
            ? allTypes.length
            : allTypes.filter(t => AUTOMATION_META[t].category === cat.key).length;
          return (
            <button
              key={cat.key}
              onClick={() => { setActiveTab(cat.key); setExpanded(null); }}
              data-testid={`tab-${cat.key}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${
                activeTab === cat.key
                  ? 'bg-[#D44E20] text-white shadow-sm'
                  : 'bg-white text-[#64748B] border border-slate-200 hover:bg-slate-50'
              }`}
            >
              <CatIcon size={13} />
              {cat.label}
              <span className={`text-[9px] px-1 py-0.5 rounded-md font-bold ${
                activeTab === cat.key ? 'bg-white/20 text-white' : 'bg-slate-100 text-[#999]'
              }`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Category Description */}
      {activeTab !== 'all' && (
        <div className="mb-4 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl">
          <p className="text-[11px] text-[#64748B]">
            {activeTab === 'transactional' && 'Si attivano automaticamente dopo un evento (acquisto, riscatto, iscrizione). Non servono configurazioni aggiuntive.'}
            {activeTab === 'scheduled' && 'Invio giornaliero programmato. Configurabili con orario, parametri e condizioni specifiche per ogni automazione.'}
            {activeTab === 'milestones' && 'Si attivano quando il cliente raggiunge un traguardo (2a visita nel mese, 10a nell\'anno). Configurabili con premi e punti bonus.'}
          </p>
        </div>
      )}

      {/* Automation cards */}
      <div className="space-y-3">
        {filteredTypes.map(type => (
          <AutomationCard
            key={type}
            type={type}
            meta={AUTOMATION_META[type]}
            config={automations?.[type] || {}}
            expanded={expanded}
            onToggleExpand={setExpanded}
            onToggle={handleToggle}
            onSave={handleSave}
            onRun={handleRun}
            saving={saving}
            running={running}
            onFieldChange={updateField}
            rewards={rewards}
            schedStatus={schedulerInfo?.automations?.[type]}
            costInfo={costData?.estimates?.[type]}
            resources={resources}
          />
        ))}
      </div>

      {/* Logs Panel */}
      {showLogs && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setShowLogs(false)}>
          <div
            className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl animate-in slide-in-from-right duration-300"
            onClick={e => e.stopPropagation()}
            data-testid="automation-logs"
          >
            <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="font-bold text-[#1A1A1A] flex items-center gap-2">
                <History size={16} className="text-[#D44E20]" /> Storico esecuzioni
              </h3>
              <button onClick={() => setShowLogs(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 text-[#64748B] hover:bg-gray-200 transition-colors">
                <ChevronUp size={16} className="rotate-90" />
              </button>
            </div>
            <div className="p-5">
              {logs.length === 0 ? (
                <p className="text-[#999] text-sm text-center py-12">Nessuna esecuzione registrata</p>
              ) : (
                <div className="space-y-2">
                  {logs.map(log => {
                    const logMeta = AUTOMATION_META[log.automation_type];
                    return (
                      <div key={log.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100" data-testid={`log-${log.id}`}>
                        {logMeta && (
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${logMeta.bgColor}`}>
                            {React.createElement(logMeta.icon, { size: 13, style: { color: logMeta.color } })}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A1A1A]">{logMeta?.title || log.automation_type}</p>
                          <p className="text-[11px] text-[#64748B] mt-0.5">
                            {log.messages_sent} msg inviati
                            {log.points_assigned > 0 && ` · ${log.points_assigned} pt`}
                          </p>
                          {log.customers_affected?.length > 0 && (
                            <p className="text-[10px] text-[#aaa] mt-0.5 truncate">{log.customers_affected.join(', ')}</p>
                          )}
                        </div>
                        <span className="text-[10px] text-[#aaa] whitespace-nowrap flex-shrink-0">
                          {log.created_at ? new Date(log.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Coupon Reminder Conversions */}
      <CouponReminderStats />
    </div>
  );
}

function CouponReminderStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get('/admin/analytics/coupon-reminders')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data || data.kpi.total_reminders === 0) return null;

  const { kpi, per_coupon } = data;

  return (
    <div className="mt-8 border-t border-slate-100 pt-6" data-testid="reminder-conversions-section">
      <PageTooltip page="automations" />
      <h2 className="text-lg font-semibold text-[#1A1A1A] tracking-tight mb-4 flex items-center gap-2">
        <BarChart3 size={18} className="text-[#D44E20]" />
        Conversioni Reminder Coupon
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="reminder-kpi-sent">
          <p className="text-[10px] text-[#64748B] uppercase font-semibold">Reminder inviati</p>
          <p className="text-2xl font-bold text-[#1A1A1A] mt-1 tabular-nums">{kpi.total_reminders}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="reminder-kpi-unique">
          <p className="text-[10px] text-[#64748B] uppercase font-semibold">Clienti raggiunti</p>
          <p className="text-2xl font-bold text-[#3B82F6] mt-1 tabular-nums">{kpi.unique_reminded}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="reminder-kpi-conversions">
          <p className="text-[10px] text-[#64748B] uppercase font-semibold">Conversioni</p>
          <p className="text-2xl font-bold text-[#10B981] mt-1 tabular-nums">{kpi.conversions}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="reminder-kpi-rate">
          <p className="text-[10px] text-[#64748B] uppercase font-semibold">Tasso conversione</p>
          <p className="text-2xl font-bold text-[#D44E20] mt-1 tabular-nums">{kpi.conversion_rate}%</p>
        </div>
      </div>
      {per_coupon.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
          <table className="w-full text-sm" data-testid="reminder-coupon-table">
            <thead>
              <tr className="bg-slate-50 text-[#64748B] text-[10px] font-semibold uppercase">
                <th className="text-left py-2.5 px-4">Coupon</th>
                <th className="text-center py-2.5 px-3">Inviati</th>
                <th className="text-center py-2.5 px-3">Clienti</th>
                <th className="text-center py-2.5 px-3">Convertiti</th>
                <th className="text-center py-2.5 px-3">Tasso</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {per_coupon.map(c => (
                <tr key={c.coupon_id} className="hover:bg-slate-50 transition-colors">
                  <td className="py-3 px-4">
                    <p className="font-semibold text-[#1A1A1A] font-mono text-xs">{c.code}</p>
                    {c.description && <p className="text-[10px] text-[#64748B] truncate max-w-[200px]">{c.description}</p>}
                  </td>
                  <td className="text-center py-3 px-3 tabular-nums text-[#1A1A1A]">{c.reminders_sent}</td>
                  <td className="text-center py-3 px-3 tabular-nums text-[#1A1A1A]">{c.unique_customers}</td>
                  <td className="text-center py-3 px-3 tabular-nums font-semibold text-[#10B981]">{c.conversions}</td>
                  <td className="text-center py-3 px-3 tabular-nums font-semibold text-[#D44E20]">{c.conversion_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
